// serviceWorkerService.ts - Handles service worker registration and updates
// This service ensures that the application always uses the latest version
// and handles cache invalidation properly

/**
 * Register the service worker
 * This should be called when the application starts
 */
export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('./sw.js')
        .then(registration => {
          console.log('ServiceWorker registration successful with scope: ', registration.scope);
          
          // Check for updates on page load and periodically
          checkForUpdates(registration);
          setInterval(() => checkForUpdates(registration), 60000); // Check every minute
          
          // Also check when the page gets focus
          window.addEventListener('focus', () => checkForUpdates(registration));
        })
        .catch(error => {
          console.error('ServiceWorker registration failed: ', error);
        });
    });
  }
}

/**
 * Check if there's a new version of the service worker
 */
function checkForUpdates(registration: ServiceWorkerRegistration) {
  registration.update()
    .then(() => {
      if (registration.waiting) {
        // New version is waiting to activate
        notifyUserOfUpdate(registration);
      }
    })
    .catch(error => {
      console.error('Failed to check for updates: ', error);
    });
}

/**
 * Notify the user that an update is available
 */
function notifyUserOfUpdate(registration: ServiceWorkerRegistration) {
  // Create or update notification element
  let notification = document.getElementById('update-notification');
  
  if (!notification) {
    notification = document.createElement('div');
    notification.id = 'update-notification';
    notification.style.position = 'fixed';
    notification.style.bottom = '20px';
    notification.style.right = '20px';
    notification.style.backgroundColor = '#4a5568';
    notification.style.color = 'white';
    notification.style.padding = '12px 20px';
    notification.style.borderRadius = '4px';
    notification.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    notification.style.zIndex = '9999';
    document.body.appendChild(notification);
  }
  
  notification.innerHTML = `
    <p style="margin: 0 0 10px 0">A new version of the site is available!</p>
    <button id="update-button" style="background: #48bb78; border: none; color: white; padding: 8px 12px; border-radius: 4px; cursor: pointer;">
      Update now
    </button>
  `;
  
  // Add click handler to update the service worker
  document.getElementById('update-button')?.addEventListener('click', () => {
    if (registration.waiting) {
      // Send message to service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Reload the page to get the new version
      window.location.reload();
    }
  });
}

/**
 * Add cache control headers to the fetch requests
 * This function can be used to wrap fetch calls to ensure proper cache control
 */
export function fetchWithCacheControl(url: string, options?: RequestInit): Promise<Response> {
  const fetchOptions: RequestInit = {
    ...options,
    headers: {
      ...options?.headers,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  };
  
  return fetch(url, fetchOptions);
}